import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {TheBorder} from "../../TheBorder";
import {TheBorderGameMode} from "../../server/TheBorderGameMode";
import {gsap} from "gsap";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TheBorderGameAssets} from "../../TheBorderGameAssets";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {BasicBitmapCountUp} from "../../components/BasicBitmapCountUp";
import {TheBorderWinConfigs} from "../../configs/TheBorderWinConfigs";
import {TheBorderGameData} from "../../server/TheBorderData";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {TheBorderSound} from "../../sound/TheBorderSound";

/**
 * Class description
 *
 * Created: 2021-11-18
 * @author jonas
 */
export class TheBorderOutroView extends PIXI.Container implements StageLayer {
    private _animation: PIXI.spine.Spine;
    private _button: ContinueButton;
    private _clickCallback: Function;
    private _youWon: PIXI.Text;
    private _countUp: BasicBitmapCountUp;

    constructor() {
        super();
        this.init();
    }

    public hideOutro(): gsap.core.Timeline {
        const tl: TimelineLite = gsap.timeline();
        tl.add(() => {
            TheBorder.backgroundElements.setMode(TheBorderGameMode.NORMAL);
            TheBorder.keypadPlugin.setWin(TheBorder.model.accumulatedRoundWin, true, TheBorder.isWinBelowStake());
        });
        tl.add(TheBorder.blackOverlay.toggleOverlay(false, 0, 0.4), "<");
        tl.to(this, {duration: 0.4, alpha: 0}, "<");

        tl.add(() => {
            this.resetSpine();
            this._animation.visible = false;
            this._countUp.visible = false;
            this._youWon.visible = false;
            this._button.visible = false;

            this.visible = false;
            this.alpha = 1;
            NolimitApplication.removeLayer(this.name);
            TheBorder.keypadPlugin.setWin(TheBorder.model.data.accumulatedRoundWin, true, TheBorder.isWinBelowStake());
        })
        return tl;
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        //Copied from Launcher/GameIntroView, so we get consistent placement of buttons
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showButton(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            this._button.enable(true);
            this._button.alpha = 0;
            this._button.visible = true;
        });
        tl.to(this._button, {duration: 0.2, alpha: 1});
        return tl;
    }

    public showOutro(callback: Function, amount: number, countDuration: number, isLessThan5Percent: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            isWinBelowStake: boolean = TheBorder.isWinBelowStake();

        this._clickCallback = callback;

        tl.add(() => {
            TheBorder.scatterSymbols.turnOffAllScatterSymbols();
            TheBorder.xNudgeSymbols.turnOffAllXNudgeSymbols();
            TheBorder.xSplitSymbols.turnOffAllXSplitSymbols();
            TheBorder.xWaysSymbols.turnOffAllXWaysSymbols();
            TheBorder.xBombSymbols.turnOffAllXBombSymbols();
            this.onResize();

            this.resetSpine();
            this.alpha = 1;
            this.visible = true;
            NolimitApplication.addLayer(this.name, this);

            TheBorder.blackOverlay.toggleOverlay(true, 0.65, 0.1);
        });
        if (isLessThan5Percent) {
            tl.add([
                new SpineTween(this._animation, 0, "Fs_Collapse"),
                () => {
                    TheBorder.soundPlayer.playSound("FS_END_STATUE_EXPLODE") //#253
                }
            ]);
        } else {
            let loopCount: number = 0,
                nextAnimName: string = "Fs_Torch_Fd_Out";
            if (amount >= (0.1 * TheBorder.getBuyFeaturePrice())) {
                loopCount = -1;
                nextAnimName = "Fs_Torch_Loop";
            } else {
                // #361
                countDuration = 13 / 30;
            }

            tl.add(new SpineTween(this._animation, 0, "Fs_Torch_in"));
            tl.add(() => {
                this.resetSpine();
                new SpineTween(this._animation, 0, nextAnimName, loopCount);
            });
        }

        tl.add(this.getCountUp(amount, countDuration, isWinBelowStake, isLessThan5Percent));

        return tl;
    }

    private getCountUp(amount: number, countDuration: number, isWinBelowStake: boolean, isLessThan5Percent: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline(),
            data: TheBorderGameData = TheBorder.model.gameData;

        tl.add(() => {
            this._youWon.visible = (!isWinBelowStake && !isLessThan5Percent);
            this._countUp.visible = true;
        });
        if (countDuration > 0) {
            tl.add(() => TheBorder.soundPlayer.startCountUp());
            tl.add(this._countUp.getCountAnimation(0, amount, countDuration));
            tl.add(() => {
                this._countUp.forceSetText(amount.toFixed(2));
            });
        } else {
            tl.add(() => this._countUp.stopAt(amount));
        }
        tl.add(() => {//#248
            !isWinBelowStake && TheBorder.soundPlayer.stopCountUp(TheBorderWinConfigs.getWinRatioConfig((data.accumulatedRoundWin / data.playedBetValue)), true);
            TheBorder.keypadPlugin.setWin(amount, true, isWinBelowStake);
        }, "+=0.2");

        return tl;
    }

    private init(): void {
        this.visible = false;

        this._animation = new PIXI.spine.Spine(TheBorder.assetLoader.getSpineAsset(TheBorderGameAssets.FS_OUTRO));
        this._animation.visible = false;
        this._animation.position.set(360);

        this._youWon = new PIXI.Text(TheBorder.apiPlugin.translations.translate("YOU WON"), new PIXI.TextStyle({
            fill: "#ffffff",
            fontFamily: OpenSans.FAMILY,
            fontWeight: FontWeight.BOLD,
            fontSize: 84
        }));
        this._youWon.visible = false;
        this._youWon.anchor.set(0.5);
        this._youWon.position.set(360, 240);

        this._countUp = new BasicBitmapCountUp({
            fontName: TheBorderGameAssets.BITMAP_FONT_FS_OUTRO_NUMBER,
            fontSize: 40,
            tint: 0xffffff
        }, 720, new PIXI.Point(0.5, 0.5));
        this._countUp.scale.set(0.8);
        this._countUp.stopAt(0);
        this._countUp.position.set(360, 390);
        this._countUp.visible = false;

        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.position.set(360, 615);
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;

        this.addChild(this._animation, this._youWon, this._countUp, this._button);

        while (this._youWon.width > 680) {
            this._youWon.style.fontSize--;
            this._youWon.updateTransform();
        }

        this.onResize();
    }

    private onButtonClick(): void {
        this._button.enable(false);
        TheBorder.soundPlayer.playSound(TheBorderSound.FS_CONTINUE_END); //#193
        TheBorder.winMultiplierView.activateMainGameMultiplier();
        this._clickCallback();
    }

    private resetSpine(): void {
        if (!this._animation.visible) {
            this._animation.visible = true;
            this._animation.updateTransform();
        }
        this._animation.state.clearTracks();
        this._animation.skeleton.setSlotsToSetupPose();
        this._animation.skeleton.setToSetupPose();
        this._animation.state.setEmptyAnimation(0, 0);
    }
}